import { useState } from "react";
import { Bell, X, Check, UserPlus, Calendar, Settings, AlertCircle, MessageSquare, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Notification types for scalable system
const NOTIFICATION_TYPES = {
  // Event Host Notifications
  NEW_RSVP: { icon: UserPlus, color: "text-green-600", bg: "bg-green-50" },
  RSVP_CANCELLED: { icon: X, color: "text-red-600", bg: "bg-red-50" },
  GUEST_CHECKIN: { icon: Check, color: "text-blue-600", bg: "bg-blue-50" },
  PREFERENCES_UPDATED: { icon: Settings, color: "text-purple-600", bg: "bg-purple-50" },
  EVENT_REMINDER: { icon: Calendar, color: "text-amber-600", bg: "bg-amber-50" },
  DIETARY_ALERT: { icon: AlertCircle, color: "text-orange-600", bg: "bg-orange-50" },
  ACCESSIBILITY_ALERT: { icon: AlertCircle, color: "text-blue-600", bg: "bg-blue-50" },
  VIP_RSVP: { icon: UserPlus, color: "text-yellow-600", bg: "bg-yellow-50" },
  
  // User/Attendee Notifications  
  EVENT_UPDATE: { icon: Calendar, color: "text-indigo-600", bg: "bg-indigo-50" },
  HOST_MESSAGE: { icon: MessageSquare, color: "text-pink-600", bg: "bg-pink-50" },
  PREFERENCE_REMINDER: { icon: Clock, color: "text-cyan-600", bg: "bg-cyan-50" },
  INTRO_REQUEST: { icon: UserPlus, color: "text-violet-600", bg: "bg-violet-50" },
  
  // System Notifications
  SYSTEM: { icon: Bell, color: "text-gray-600", bg: "bg-gray-50" }
};

const getNotificationConfig = (type) => {
  return NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.SYSTEM;
};

const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return "Just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return date.toLocaleDateString();
};

// Separate component to avoid re-render issues
const NotificationItem = ({ notification, onMarkRead, onDismiss }) => {
  const config = getNotificationConfig(notification.type);
  const Icon = config.icon;

  return (
    <div 
      className={`p-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors cursor-pointer ${
        !notification.read ? "bg-blue-50/30" : ""
      }`}
      onClick={() => onMarkRead?.(notification.id)}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{notification.title}</p>
          <p className="text-xs text-gray-600 line-clamp-2">{notification.message}</p>
          <p className="text-[10px] text-gray-400 mt-1">{formatTime(notification.timestamp)}</p>
        </div>
        {onDismiss && (
          <button 
            onClick={(e) => { e.stopPropagation(); onDismiss(notification.id); }}
            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X className="w-3.5 h-3.5 text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );
};

const NotificationCenter = ({ 
  notifications = [], 
  onDismiss,
  onDismissAll,
  onMarkRead,
  onViewAll,
  variant = "dropdown", // "dropdown" or "panel"
  maxDisplay = 5
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const unreadCount = notifications.filter(n => !n.read).length;

  if (variant === "panel") {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-gray-700" />
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-xs">
                {unreadCount} new
              </Badge>
            )}
          </div>
          {notifications.length > 0 && onDismissAll && (
            <Button variant="ghost" size="sm" onClick={onDismissAll} className="text-xs text-gray-500">
              Clear all
            </Button>
          )}
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.slice(0, maxDisplay).map((notification) => (
              <NotificationItem 
                key={notification.id} 
                notification={notification} 
                onMarkRead={onMarkRead}
                onDismiss={onDismiss}
              />
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No notifications yet</p>
            </div>
          )}
        </div>
        
        {notifications.length > maxDisplay && (
          <div className="p-3 border-t border-gray-100 text-center">
            <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              View all ({notifications.length})
            </button>
          </div>
        )}
      </div>
    );
  }

  // Dropdown variant
  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-gray-200 shadow-lg z-50 overflow-hidden">
            <div className="p-3 border-b border-gray-100 flex items-center justify-between">
              <span className="font-semibold text-sm text-gray-900">Notifications</span>
              {unreadCount > 0 && (
                <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-xs">
                  {unreadCount} new
                </Badge>
              )}
            </div>
            
            <div className="max-h-72 overflow-y-auto">
              {notifications.length > 0 ? (
                notifications.slice(0, maxDisplay).map((notification) => (
                  <NotificationItem 
                    key={notification.id} 
                    notification={notification}
                    onMarkRead={onMarkRead}
                    onDismiss={onDismiss}
                  />
                ))
              ) : (
                <div className="p-6 text-center text-gray-500">
                  <Bell className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                  <p className="text-xs">No notifications</p>
                </div>
              )}
            </div>
            
            {notifications.length > 0 && (
              <div className="p-2 border-t border-gray-100 flex justify-between">
                <button 
                  onClick={onDismissAll}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </button>
                <button 
                  onClick={() => { onViewAll?.(); setIsOpen(false); }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  View all
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;

// Export notification types for use in other components
export { NOTIFICATION_TYPES };
